/**
 * Perfil de registro BRACELL (paridade com SF-APP).
 */

export const BRACELL_ENTERPRISE_KEY = "BRACELL";

export function isBracellEnterprise() {
  if (typeof window === "undefined" || !window.localStorage) return false;
  return (localStorage.getItem("enterprise") || "").trim() === BRACELL_ENTERPRISE_KEY;
}

/** Idade do talhão em anos: vários nomes possíveis na API. */
export function getPlotAgeInYears(plot) {
  if (!plot || typeof plot !== "object") return null;
  const v =
    plot.idade ??
    plot.idade_anos ??
    plot.idadeAnos ??
    plot.age ??
    plot.plot_age ??
    plot.plotAge;
  if (v == null || v === "") return null;
  const n =
    typeof v === "string" ? parseFloat(String(v).replace(",", ".")) : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function resolveBracellBeetleCostalamaitaThresholds(ageYears) {
  const adult = { lowMax: 50, mediumMax: 80.0001, zeroEpsilon: 0.00001 };
  if (ageYears == null || !Number.isFinite(ageYears)) {
    return adult;
  }
  if (ageYears < 0.6) {
    return { lowMax: 5.0001, mediumMax: 10.0001, zeroEpsilon: 0.00001 };
  }
  if (ageYears <= 0.9) {
    return { lowMax: 15, mediumMax: 30.0001, zeroEpsilon: 0.00001 };
  }
  return adult;
}

export const BRACELL_CATERPILLAR_THRESHOLDS = {
  zeroEpsilon: 0.000001,
  lowMax: 3.1,
  mediumMax: 8.1,
};

export const BRACELL_LEPTOCYBE_THRESHOLDS = {
  muitoBaixaMax: 1,
  baixaMax: 5,
  mediaMax: 10,
  zeroEpsilon: 0.00001,
};

/**
 * Concha: ordem do app — f = folhas totais, dens = conchas/f.
 */
export function diagnoseBracellConcha(sumConchas, rowCount, leavesPerPlant = 1) {
  const f = rowCount * leavesPerPlant;
  if (f <= 0) {
    return { diagnostic: "Muito Baixo", prob: 0, levelKey: "none" };
  }
  const dens = sumConchas / f;
  const prob = parseFloat(dens.toFixed(2));
  const densAt2 = parseFloat(dens.toFixed(2));

  if (dens < 1) {
    return { diagnostic: "Muito Baixo", prob, levelKey: "muitoBaixo" };
  }
  if (densAt2 === 1) {
    return { diagnostic: "Baixo", prob, levelKey: "baixo" };
  }
  if (dens <= 5) {
    return { diagnostic: "Médio", prob, levelKey: "medio" };
  }
  return { diagnostic: "Alto", prob, levelKey: "alto" };
}

function tierInsetosPercevejo(insPer) {
  if (insPer <= 3) return 0;
  if (insPer <= 5) return 1;
  return 2;
}

function tierOvosPercevejo(massPer) {
  if (massPer <= 2) return 0;
  return 1;
}

const PERCEVEJO_TIER_LABEL = ["Baixo", "Médio", "Alto"];

export function diagnoseBracellPercevejo(sumInsetos, sumMassas, rowCount) {
  const folhas = rowCount * 1;
  if (folhas <= 0) {
    return { diagnostic: "Baixo", prob: 0, levelKey: "none" };
  }
  const insPer = sumInsetos / folhas;
  const massPer = sumMassas / folhas;
  const ti = tierInsetosPercevejo(insPer);
  const to = tierOvosPercevejo(massPer);
  const tier = Math.max(ti, to);
  const prob = parseFloat(insPer.toFixed(2));
  const diagnostic = PERCEVEJO_TIER_LABEL[tier] || "Alto";
  const levelKey = ["baixo", "medio", "alto"][tier] || "alto";
  return { diagnostic, prob, levelKey };
}

export function diagnoseBracellBeetle(mean, ageYears) {
  const { lowMax, mediumMax, zeroEpsilon } =
    resolveBracellBeetleCostalamaitaThresholds(ageYears);
  const prob = Number.isFinite(mean) ? parseFloat(mean.toFixed(2)) : 0;

  if (!Number.isFinite(mean) || mean <= zeroEpsilon) {
    return { diagnostic: "Baixo", prob: Number.isFinite(mean) ? prob : 0, levelKey: "none" };
  }
  if (mean < lowMax) {
    return { diagnostic: "Baixo", prob, levelKey: "baixo" };
  }
  if (mean < mediumMax) {
    return { diagnostic: "Médio", prob, levelKey: "medio" };
  }
  return { diagnostic: "Alto", prob, levelKey: "alto" };
}

export function diagnoseBracellCaterpillar(percentualGlobal) {
  const { zeroEpsilon, lowMax, mediumMax } = BRACELL_CATERPILLAR_THRESHOLDS;
  const p = Number(percentualGlobal);
  if (!Number.isFinite(p) || p <= zeroEpsilon) {
    return {
      diagnostic: "Baixo",
      prob: Number.isFinite(p) ? parseFloat(p.toFixed(2)) : 0,
      levelKey: "none",
    };
  }
  const prob = parseFloat(p.toFixed(2));
  if (p < lowMax) {
    return { diagnostic: "Baixo", prob, levelKey: "baixo" };
  }
  if (p < mediumMax) {
    return { diagnostic: "Médio", prob, levelKey: "medio" };
  }
  return { diagnostic: "Alto", prob, levelKey: "alto" };
}

export function diagnoseBracellLeptocybe(mean, plantCount) {
  const { zeroEpsilon, muitoBaixaMax, baixaMax, mediaMax } =
    BRACELL_LEPTOCYBE_THRESHOLDS;
  const m = Number(mean);
  const prob = Number.isFinite(m) ? parseFloat(String(m)) : 0;

  if (plantCount <= 0 || !Number.isFinite(m) || m <= zeroEpsilon) {
    return { diagnostic: "Baixo", prob: Number.isFinite(m) ? prob : 0, levelKey: "none" };
  }
  if (m <= muitoBaixaMax) {
    return { diagnostic: "Muito Baixa", prob, levelKey: "muitoBaixa" };
  }
  if (m <= baixaMax) {
    return { diagnostic: "Baixa", prob, levelKey: "baixa" };
  }
  if (m <= mediaMax) {
    return { diagnostic: "Média", prob, levelKey: "media" };
  }
  return { diagnostic: "Alta", prob, levelKey: "alta" };
}

export const BRACELL_DANO_SEVERIDADE = [
  "Sem Dano",
  "Baixo",
  "Médio",
  "Alto",
];
